/** A box on screen. */
export interface Box { left: number; top: number; width: number; height: number }

const GAP = 10;
const EDGE = 8;

/**
 * Where a guide card goes for the control it describes: beside a row in an open menu
 * (the menu is in the way above and below), otherwise above the control — the dock and the
 * tool strip sit at the bottom — clear of every bar in the way (`obstacles`: the dock the
 * button sits in, the strip floating over it), else below it; centred on the control and
 * kept inside the window. Sitting on the strip it covered the controls it was explaining.
 */
export function placeBeside(
  control: Box,
  card: { width: number; height: number },
  view: { width: number; height: number },
  inMenu: boolean,
  obstacles: readonly Box[] = [],
): { x: number; y: number } {
  const clampX = (x: number) => Math.max(EDGE, Math.min(x, view.width - card.width - EDGE));
  const clampY = (y: number) => Math.max(EDGE, Math.min(y, view.height - card.height - EDGE));
  if (inMenu) {
    const right = control.left + control.width + GAP;
    const x = right + card.width <= view.width - EDGE ? right : control.left - GAP - card.width;
    return { x: clampX(x), y: clampY(control.top + control.height / 2 - card.height / 2) };
  }
  const x = clampX(control.left + control.width / 2 - card.width / 2);
  // Climb over whatever the card would land on, a bar at a time.
  let top = control.top;
  for (let moved = true; moved; ) {
    moved = false;
    for (const bar of obstacles) {
      const across = bar.left < x + card.width && bar.left + bar.width > x;
      const inBand = bar.top < top && bar.top + bar.height > top - GAP - card.height;
      if (across && inBand) {
        top = bar.top;
        moved = true;
      }
    }
  }
  const above = top - GAP - card.height;
  return { x, y: above >= EDGE ? above : clampY(control.top + control.height + GAP) };
}
